'use client';
import Image from 'next/image';
import { IconCross } from '@/icons/IconCross';
import { ICollectableCoupon } from '@/interfaces/collectableCoupon';

export interface CouponCardProps {
  coupon: ICollectableCoupon;
  /** Note: used by the modal's card-level close icon when `showCloseIcon` is true. */
  showCloseIcon?: boolean;
  onClose?: () => void;
  onCollect: (couponId: string) => void;
  isCollecting: boolean;
  /** When `isCollecting` is true, only the matching id is shown as "..." — passed by the modal. */
  collectingId?: string;
  /** Extra className for the outermost card container. */
  className?: string;
}

/**
 * Single coupon card. Used by both the modal grid and the single-coupon
 * Popup. The card chrome (image + name + Collect button) is identical
 * across both surfaces.
 */
export const CouponCard = ({
  coupon,
  showCloseIcon = false,
  onClose,
  onCollect,
  isCollecting,
  collectingId,
  className = '',
}: CouponCardProps) => {
  const isThisCardCollecting = isCollecting && collectingId === coupon.Id;
  const showCollectingLabel = isThisCardCollecting;

  return (
    <div
      className={`relative flex w-[230px] shrink-0 flex-col overflow-hidden rounded-xl border border-[#E1E4EA] bg-white shadow-sm xs:w-[180px] sm:w-[200px] md:w-[240px] ${className}`.trim()}
      data-testid={`floating-coupon-slide-${coupon.Id}`}
    >
      {showCloseIcon && onClose && (
        <span
          data-testid="floating-coupon-card-close"
          onClick={onClose}
          role="button"
          aria-label="Close coupons"
          className="card-close-icon absolute right-0 top-0 z-[1000000] ml-auto flex aspect-square w-8 cursor-pointer rounded-bl-[15px] bg-white bg-opacity-60 pb-0.5 pl-0.5 md:w-10"
          style={{ boxShadow: '0 0 5px 3px rgb(0 0 0 / 25%)' }}
        >
          <IconCross className="m-auto block w-3 md:w-4" />
        </span>
      )}
      {/* Image Box */}
      <div className="relative aspect-square w-full bg-[#D3131A]">
        <Image fill src={coupon.CouponImageUrl} alt={coupon.CouponName} className="object-cover" />
      </div>
      {/* White Action Bar Footer */}
      <div className="flex items-center justify-between gap-2 rounded-b-xl bg-[#F8FAFC] p-3">
        <div className="flex min-w-0 flex-1 flex-col">
          <span
            className="truncate font-bold uppercase leading-none text-[#D3131A] coupon-name"
            style={{ fontFamily: 'Figtree, sans-serif', fontStyle: 'Bold', lineHeight: '100%', letterSpacing: '0%' }}
          >
            {coupon.CouponName}
          </span>
        </div>
        <button
          onClick={() => onCollect(coupon.Id)}
          disabled={isThisCardCollecting}
          className="collect-button rounded bg-[#FACC15] font-bold text-black shadow-sm transition-colors hover:bg-[#E6BD00] disabled:opacity-50"
        >
          <span
            className="collect-button-label"
            style={{
              fontFamily: 'Figtree, sans-serif',
              fontWeight: 700,
              fontStyle: 'Bold',
              lineHeight: '120%',
              letterSpacing: '0%',
              opacity: 1,
            }}
          >
            {showCollectingLabel ? '...' : 'Collect'}
          </span>
        </button>
      </div>
    </div>
  );
};